import { Injectable } from '@angular/core';

import { BetslipDisplayMode } from '@frontend/sports/types/components/bet-placement';

import { isLinear } from '../../../base/utils/linear-utils';
import { BetslipType } from '../../../core/betslip-type';
import { BetslipBetBuilderPick } from '../../../core/picks/betslip-bet-builder-pick';
import { BetslipPick } from '../../../core/picks/betslip-pick';
import { IComboBetPickState } from '../../combo-bet/state';
import { getPickEachWayInfo, isStartingPriceSelected, isXCastPick } from '../../picks/services/betslip-pick-helpers';
import { isFreebetToken } from '../../reward-tokens/services/reward-tokens.utils';
import { hasCriticalBetslipAndTypeErrorForType, hasCriticalSlipErrorsForType } from '../../validation/services/utils/betslip-errors-utils';
import { ICalculatorBetslipInput } from '../models';
import { ComboSlipType, SingleSlipType, SlipType } from '../slip-type';
import BaseInputBuilder, { BetslipStateInput, ComboInputBuilderGeneratorOptions } from './base-input-builder';
import { getComboSlipId } from './slip.utils';

// Returns all picks from ComboBet state that are selected and are present in pickList
const defaultComboPicksSelector = (betslipState: BetslipStateInput) =>
    Object.entries(betslipState.types.comboBet.picks).filter(
        ([key, value]) =>
            value.isSelected &&
            (betslipState.base.displayMode !== BetslipDisplayMode.Linear || !value.isLocked) &&
            betslipState.picks.pickList.some((p) => p.id.toString() === key),
    );
@Injectable()
export default class ComboBetInputBuilder extends BaseInputBuilder {
    *getBetGeneratorInput(
        betslipState: BetslipStateInput,
        options?: ComboInputBuilderGeneratorOptions<IComboBetPickState>,
    ): Iterable<ICalculatorBetslipInput> {
        const picksSelector = options?.picksSelector ?? defaultComboPicksSelector;

        const comboBetPickIds = picksSelector(betslipState).map(([key, _]) => key);
        const rewardTokenId = betslipState.types.comboBet.rewardTokenId;
        const rewardToken = this.getSelectedTokenIfEligible(rewardTokenId, betslipState.rewardTokens, betslipState.types, {
            betslipType: BetslipType.Combo,
        });

        const stake = isFreebetToken(rewardToken) ? rewardToken.amount : betslipState.types.comboBet.stake;
        const comboBetPicks = comboBetPickIds.map((id) => betslipState.picks.pickList.find((p) => p.id.isEqual(id))!);
        yield {
            slipId: getComboSlipId(),
            items: comboBetPickIds.map((id) => {
                const pick = betslipState.picks.pickList.find((p) => p.id.isEqual(id))!;

                return {
                    ...this.getPickInfo(pick),
                    eachWayInfo: betslipState.types.comboBet.isEachWay ? getPickEachWayInfo(pick) : null,
                    isBanker: false,
                    isXCast: isXCastPick(pick),
                    isStartingPrice: isStartingPriceSelected(pick),
                };
            }),
            rewardToken,
            stake,
            hasError:
                hasCriticalBetslipAndTypeErrorForType(betslipState.errors, BetslipType.Combo) ||
                hasCriticalSlipErrorsForType(betslipState.errors, BetslipType.Combo, betslipState.types, isLinear(betslipState)),
            system: this.resolveSlipType(comboBetPicks, !!options?.isSportcastAsComboEnabled),
        };
    }

    private resolveSlipType(comboBetPicks: BetslipPick[], isSportcastAsComboEnabled: boolean): SlipType {
        const isSingleBetBuilderPick = comboBetPicks.length === 1 && BetslipBetBuilderPick.isPick(comboBetPicks[0]);

        return isSportcastAsComboEnabled && isSingleBetBuilderPick ? new SingleSlipType() : new ComboSlipType();
    }
}
